import { useEffect, useState } from "react";
import axios from "axios";
import baseURL from "../../config/api";
import { FaVideo } from "react-icons/fa";
import { getExercise } from "../../services/Practice";
import { getContent, getVideo } from "../../services/SubLesson";
import { getEnrolledCourses, post_EnrollCourse } from "../../services/Course";
import { getEnrolledClassesCourses } from "../../services/Class";
import { getEnrolledParnentCourses } from "../../services/Parent";
import useConfirm from "../../hook/useConfirm";
import { VideoPlayer } from "./VideoPlayer";
import { ShowCompleteContent } from "../../components/ShowCompleteContent";

type QuizProps = {
    userId: string;
    quizId: number;
    courseId: number;
    subLessonId: number;
    exerciseId?: number; // ← new
    onFinish: () => void;
    isExercise?: boolean;
};

export const Quiz = ({
    userId,
    quizId,
    courseId,
    subLessonId,
    exerciseId,
    onFinish,
    isExercise = false,
}: QuizProps) => {
    const { setConfirmPopup, setData: setConfirmData }: any = useConfirm();
    const [step, setStep] = useState<"intro" | "quiz" | "result">("intro");
    const [questions, setQuestions] = useState<any[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [answers, setAnswers] = useState<any[]>([]);
    const [score, setScore] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [relatedContents, setRelatedContents] = useState<any[]>([]);
    const [exerciseContents, setExerciseContents] = useState<any[]>([]);
    const [exerciseContentIsLoaded, setExerciseContentIsLoaded] = useState<boolean>(false);
    const [relatedExerciseVideos, setRelatedExerciseVideos] = useState<any[]>([]);
    const [exerciseVideos, setExerciseVideos] = useState<any[]>([]);
    const [exerciseVideoIsLoaded, setExerciseVideoIsLoaded] = useState<boolean>(false);
    const [selectedOptions, setSelectedOptions] = useState<number[]>([]);
    const [feedback, setFeedback] = useState<boolean | null>(null);
    const [allEnrolledCourses, setAllEnrolledCourses] = useState<number[]>([]);
    const currentQ = questions[currentIndex];
    const isMultiple = currentQ?.questioType === "MultipleChoice";
    const [selectedVideo, setSelectedVideo] = useState<number | null>(null);
    const [selectedContent, setSelectedContent] = useState<number | null>(null);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, [quizId, isExercise]);

    useEffect(() => {
        if (!isExercise && Number(userId)) {
            const fetchData = async () => {
                try {

                    const [data1, data2, data3] = await Promise.all([
                        getEnrolledCourses(Number(userId)),
                        getEnrolledClassesCourses(Number(userId)),
                        getEnrolledParnentCourses(Number(userId)),
                    ]);


                    const ids1 = data1?.map((item: any) => item.id) || [];
                    const ids2 = data2?.map((item: any) => item.courseId) || [];
                    const ids3 = data3?.map((item: any) => item.id) || [];


                    const mergedIds = Array.from(new Set([...ids1, ...ids2, ...ids3]));


                    setAllEnrolledCourses(mergedIds);
                } catch (error) {
                    console.error("Error fetching data:", error);
                }
            };

            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isExercise])

    useEffect(() => {
        const fetchQuizData = async () => {
            try {
                let res;
                let data;
                let questionsArray: any[] = [];

                if (isExercise && exerciseId) {

                    res = await axios.get(`${baseURL}quizzes/GetQuizzesRelatedExercises/${exerciseId}`);
                    data = res.data;
                    questionsArray = Array.isArray(data)
                        ? data.flatMap((item: any) => item.questions || [])
                        : data.questions || [];

                    const excersiseData = await getExercise(exerciseId)
                    setRelatedExerciseVideos(excersiseData?.videoRelatedExercises || [])
                    setRelatedContents(excersiseData?.contentRelatedExercises || [])

                } else if (!isExercise && quizId) {
                    res = await axios.get(`${baseURL}quizzes/${quizId}`);
                    data = res.data;

                    questionsArray = data.questions || [];
                } else {
                    return;
                }

                const sortedQuestions = questionsArray.sort(
                    (a: any, b: any) => a.orderNumber - b.orderNumber
                );

                setQuestions(sortedQuestions);

            } catch (err) {
                console.error("Error fetching quiz/exercise data:", err);
                setQuestions([]);
            }
        };

        fetchQuizData();
    }, [quizId, exerciseId, isExercise]);

    useEffect(() => {
        const fetchAllVideos = async () => {
            if (!relatedExerciseVideos?.length) return;

            try {
                setExerciseVideoIsLoaded(false);
                const data = await Promise.all(
                    relatedExerciseVideos.map((item) =>
                        getVideo(item?.videoId)
                    )
                );


                setExerciseVideos(data);
                setExerciseVideoIsLoaded(true);
            } catch (error) {
                console.error("Error fetching related videos:", error);
            }
        }

        fetchAllVideos();
    }, [relatedExerciseVideos])

    useEffect(() => {
        const fetchAllContents = async () => {
            if (!relatedContents?.length) return;

            try {
                setExerciseContentIsLoaded(false);
                const data = await Promise.all(
                    relatedContents.map((item) =>
                        getContent(item?.contentId)
                    )
                );


                setExerciseContents(data);
                setExerciseContentIsLoaded(true);
            } catch (error) {
                console.error("Error fetching related contents:", error);
            }
        };
        fetchAllContents();

    }, [relatedContents])

    useEffect(() => {
        setStep("intro");
        setCurrentIndex(0);
        setSelectedOptions([]);
        setFeedback(null);
        setScore(0);
    }, [quizId, exerciseId]);

    const handleOptionChange = (answerId: number) => {
        if (feedback !== null) return;

        if (isMultiple) {
            setSelectedOptions((prev) =>
                prev.includes(answerId)
                    ? prev.filter((id) => id !== answerId)
                    : [...prev, answerId]
            );
        } else {
            setSelectedOptions([answerId]);
        }
    };

    const handleSubmitExerciseQuestion = () => {
        if (selectedOptions.length === 0) return;

        const selectedAnswers = currentQ.answers.filter((ans: any) =>
            selectedOptions.includes(ans.id)
        );

        const correctAnswers = currentQ.answers.filter((ans: any) => ans.isCorrect);
        let correctCount = 0;

        selectedAnswers.forEach((ans: any) => {
            if (ans.isCorrect) correctCount++;
        });

        const questionCorrect =
            correctCount === correctAnswers.length &&
            selectedAnswers.length === correctAnswers.length;

        if (questionCorrect) setScore((prev) => prev + 1);

        setFeedback(questionCorrect);

        setAnswers((prev) => [
            ...prev,
            ...selectedAnswers.map((ans: any) => ({
                questionId: currentQ.id,
                answerId: ans.id,
                answerText: ans.answerText,
                isCorrect: ans.isCorrect,
            })),
        ]);
    };

    const handleNextQuestion = () => {
        if (selectedOptions.length > 0 && currentQ) {
            const selectedAnswers = currentQ.answers.filter((ans: any) =>
                selectedOptions.includes(ans.id)
            );

            const correctAnswers = currentQ.answers.filter((ans: any) => ans.isCorrect);

            if (!isExercise) {
                const questionCorrect =
                    selectedAnswers.length === correctAnswers.length &&
                    selectedAnswers.every((ans: any) => ans.isCorrect);

                if (questionCorrect) setScore((prev) => prev + 1);
            }

            if (!isExercise) {
                setAnswers(prev => [
                    ...prev,
                    ...selectedAnswers.map((ans: any) => ({
                        questionId: currentQ.id,
                        answerId: ans.id,
                        answerText: ans.answerText,
                        isCorrect: ans.isCorrect,
                    })),
                ]);
            }
        }

        if (isExercise) {
            setSelectedOptions([]);
            setFeedback(null);
        }


        if (currentIndex + 1 < questions.length) {
            setCurrentIndex(prev => prev + 1);
        } else {
            setStep("result");
        }
    };

    const handleSubmitQuiz = async () => {
        if (allEnrolledCourses.includes(courseId)) {
            setSubmitting(true);

            try {
                const submissionRes = await axios.post(`${baseURL}submissions`, {
                    userId,
                    quizId,
                    submittedAt: new Date().toISOString(),
                    score: score,
                });

                const submissionId = submissionRes.data;

                await Promise.all(answers.map(ans =>
                    axios.post(`${baseURL}submissionAnswers`, {
                        submissionId,
                        questionId: ans.questionId,
                        answerId: ans.answerId,
                        answerText: ans.answerText,
                        isCorrect: ans.isCorrect,
                    })
                ));
                onFinish();
            } catch (err) {
                console.error("Error submitting quiz:", err);
            } finally {
                setSubmitting(false);
            }
        }
        else {
            setConfirmPopup(true);
            setConfirmData({
                title: "To submit this quiz you have to enroll this course.",
                confirmBtn: "Ok",
                isCancelBtn: true,
                isConfirm: async (callback: any) => {
                    setSubmitting(true);
                    post_EnrollCourse({
                        courseId,
                        userId,
                        enrolledAt: new Date().toISOString(),
                    }, Number(userId))
                        .then(async (data) => {
                            try {
                                const submissionRes = await axios.post(`${baseURL}submissions`, {
                                    userId,
                                    quizId,
                                    submittedAt: new Date().toISOString(),
                                    score: score,
                                });

                                const submissionId = submissionRes.data;

                                await Promise.all(answers.map(ans =>
                                    axios.post(`${baseURL}submissionAnswers`, {
                                        submissionId,
                                        questionId: ans.questionId,
                                        answerId: ans.answerId,
                                        answerText: ans.answerText,
                                        isCorrect: ans.isCorrect,
                                    })
                                ));
                                onFinish();
                            } catch (err) {
                                console.error("Error submitting quiz:", err);
                            } finally {
                                callback();
                                setSubmitting(false);
                            }
                        })
                        .catch((error) => {
                            console.error(error)
                        })
                }
            })
        }
    };

    const RelatedSection = () => (
        <div className="mt-6 space-y-4">
            {(exerciseVideoIsLoaded && exerciseVideos.length > 0) && (
                <div>
                    <h4 className="font-bold mb-2">Exercise Related Videos</h4>
                    <div className="space-y-3">
                        {exerciseVideos.map((video) => (
                            <div
                                key={video?.id}
                                className="flex items-center gap-3 border-b pb-2 cursor-pointer"
                            >
                                <FaVideo className="text-primary text-lg" />
                                <span
                                    onClick={() => setSelectedVideo(video?.id)}
                                    className="font-medium text-primary hover:underline"
                                >
                                    {video?.title}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {(exerciseContentIsLoaded && exerciseContents.length > 0) && (
                <div>
                    <h4 className="font-bold mb-2">📄 Related Content</h4>
                    <ul className="list-disc list-inside text-primary">
                        {exerciseContents.map((c) => (
                            <div
                                key={c?.id}
                                className="flex items-center gap-3 border-b pb-2 cursor-pointer"
                            >
                                <span
                                    onClick={() => setSelectedContent(c?.id)}
                                    className="font-medium text-primary hover:underline"
                                >
                                    {c?.title}
                                </span>
                            </div>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );

    if (step === "intro") {
        return (
            <div className="p-6 bg-white rounded shadow">
                <h2 className="text-xl font-bold mb-2">
                    {isExercise ? "Ready to practice?" : "Time for a quiz?"}
                </h2>
                <p className="text-gray-600 mb-6">
                    {isExercise
                        ? "Okay, show us what you can do!"
                        : "Get ready for questions on the lesson so far."}
                </p>
                <div className="text-right">
                    <button
                        className="px-4 py-2 bg-primary text-white rounded"
                        onClick={() => setStep("quiz")}
                    >
                        Let’s go →
                    </button>
                </div>
            </div>
        );
    }

    if (step === "quiz") {
        if (!currentQ) {
            return (
                <div className="flex items-center justify-center h-64">
                    <div className="flex flex-col items-center gap-3">
                        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                        <p className="text-gray-600 font-medium">Loading questions...</p>
                    </div>
                </div>
            );
        }

        return (
            <>
                {(!selectedVideo && !selectedContent) && (
                    <div className="p-6 bg-white rounded shadow">
                        <h3 className="text-lg font-semibold mb-4">
                            Q{currentIndex + 1}. {currentQ.questionText}
                        </h3>

                        <div className="space-y-2">
                            {currentQ.answers.map((ans: any) => {
                                const selected = selectedOptions.includes(ans.id);
                                const showCorrect = feedback !== null && ans.isCorrect && !selected;

                                const answerClasses =
                                    isExercise && feedback !== null
                                        ? selected
                                            ? ans.isCorrect
                                                ? "bg-green-100 border-green-400"
                                                : "bg-red-100 border-red-400"
                                            : showCorrect
                                                ? "bg-green-100 border-green-400"
                                                : ""
                                        : "";

                                return (
                                    <label
                                        key={ans.id}
                                        className={`flex items-center gap-2 p-2 border rounded cursor-pointer ${answerClasses}`}
                                    >
                                        <input
                                            type={isMultiple ? "checkbox" : "radio"}
                                            name={`question-${currentQ.id}`}
                                            value={ans.id}
                                            checked={selected}
                                            onChange={() => handleOptionChange(ans.id)}
                                            disabled={feedback !== null}
                                        />
                                        {ans.answerText}

                                        {isExercise && feedback !== null && selected && (
                                            <span className="ml-2 font-semibold">
                                                {ans.isCorrect ? "✅ Correct" : "❌ Wrong"}
                                            </span>
                                        )}

                                        {isExercise && feedback !== null && showCorrect && !selected && (
                                            <span className="ml-2 font-semibold text-green-700">
                                                ✅ Correct Answer
                                            </span>
                                        )}
                                    </label>
                                );
                            })}

                            {isExercise && feedback !== null && currentQ.explanationText && (
                                <p className="mt-2 text-gray-700 italic bg-gray-100 p-2 rounded">
                                    💡 {currentQ.explanationText}
                                </p>
                            )}
                        </div>

                        <div className="mt-4 text-right">
                            {feedback === null && isExercise ? (
                                <button
                                    className="px-4 py-2 bg-primary text-white rounded"
                                    onClick={() => {
                                        handleSubmitExerciseQuestion();
                                    }}
                                >
                                    Check
                                </button>
                            ) : currentIndex < questions.length ? (
                                <button
                                    className="px-4 py-2 bg-primary text-white rounded"
                                    onClick={() => {
                                        handleNextQuestion()
                                    }}
                                >
                                    Next →
                                </button>
                            ) : (
                                <button
                                    className="px-4 py-2 bg-green-600 text-white rounded"
                                    onClick={() => setStep("result")}
                                >
                                    Finish
                                </button>
                            )}
                        </div>

                        {isExercise && <RelatedSection />}
                    </div>
                )}

                {selectedVideo && (
                    <VideoPlayer
                        videoId={selectedVideo}
                        onClose={() => setSelectedVideo(null)}
                    />
                )}

                {selectedContent && (
                    <ShowCompleteContent contentId={selectedContent} onClose={() => setSelectedContent(null)} />
                )}
            </>
        );
    }

    if (step === "result") {
        return (
            <div className="p-6 bg-white rounded shadow text-center">
                <h2 className="text-xl font-bold mb-4">
                    {isExercise ? "Great effort! 🚀" : "Quiz Completed 🎉"}
                </h2>

                {!isExercise && (
                    <div className="flex justify-center">
                        <button
                            className="px-4 py-2 bg-green-600 text-white rounded flex items-center justify-center"
                            onClick={handleSubmitQuiz}
                            disabled={submitting}
                        >
                            {submitting ? (
                                <span className="loader border-2 border-t-2 border-white rounded-full w-4 h-4 animate-spin mr-2"></span>
                            ) : null}
                            {submitting ? "Submitting..." : "Submit Results"}
                        </button>
                    </div>
                )}

                {isExercise && (
                    <div>
                        <p className="text-gray-600 mb-4">
                            Every step forward makes you stronger. Keep practicing!
                        </p>
                        <p className="font-semibold text-lg">
                            ✅ Correct Answers: {score} / {questions.length}
                        </p>
                    </div>
                )}
            </div>
        );
    }

    return null;
};
